import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "react-router";

import { listCompanies } from "../../api/companies";
import type { CompanyListItem } from "../../api/companies";
import { mapPoints } from "../../api/map";
import type { MapPoint } from "../../api/map";
import { Button, buttonClass } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { CompanyRow } from "../../components/CompanyRow";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { Sheet } from "../../components/Dialog";
import { SearchIcon } from "../../components/icons";
import { StatusChip } from "../../components/StatusChip";
import { nl } from "../../i18n/nl";
import { mapConfig } from "../../lib/mapConfig";
import { DESKTOP_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { useMe } from "../auth/session";
import { COMPANIES_KEY } from "../companies/queries";
import { useSearch } from "../search/SearchProvider";
import styles from "./map.module.css";
import type { Bounds } from "./MapView";

// MapLibre is large: load it only on the map screen.
const MapView = lazy(() => import("./MapView").then((m) => ({ default: m.MapView })));

const t = nl.map;
const FISHED = new Set(["nee", "volzet"]);

function inside(point: MapPoint, bounds: Bounds | null): boolean {
  if (!bounds) return true;
  return (
    point.lat >= bounds.south && point.lat <= bounds.north && point.lon >= bounds.west && point.lon <= bounds.east
  );
}

function Row({ company, selected, onSelect }: { company: CompanyListItem; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <li>
      <CompanyRow
        company={{
          id: company.id,
          name: company.name,
          status: company.status,
          hue: company.avatar_hue,
          detail: [company.municipality, company.distance_km !== null ? nl.companies.distance(company.distance_km) : null]
            .filter(Boolean)
            .join(" · "),
        }}
        selected={selected}
        onSelect={onSelect}
      />
    </li>
  );
}

function SelectedCard({ company, onClose }: { company: CompanyListItem; onClose: () => void }) {
  return (
    <section className={styles.card} aria-labelledby="map-card-title">
      <StatusChip status={company.status} />
      <h2 id="map-card-title" className={styles.cardTitle}>
        {company.name}
      </h2>
      <p className={styles.muted}>
        {[company.municipality, company.distance_km !== null ? nl.companies.distance(company.distance_km) : null]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <div className={styles.cardActions}>
        <Link className={buttonClass("primary")} to={`/bedrijven/${company.id}`}>
          {t.open}
        </Link>
        <Button variant="ghost" onClick={onClose}>
          {t.close}
        </Button>
      </div>
    </section>
  );
}

export function MapPage() {
  const config = mapConfig();
  const me = useMe();
  const hasHome = Boolean(me.data?.home_municipality);
  const [near, setNear] = useState(false);
  const [onlyKans, setOnlyKans] = useState(false);
  const [fished, setFished] = useState(true);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const openSearch = useSearch();

  const points = useQuery({ queryKey: [...COMPANIES_KEY, "map"], queryFn: mapPoints, enabled: Boolean(config.tileUrl) });
  const query = { near: near && hasHome, status: onlyKans ? ("kans" as const) : undefined, sort: hasHome ? ("distance" as const) : ("name" as const), limit: 100 };
  const list = useQuery({ queryKey: [...COMPANIES_KEY, "map-list", query], queryFn: () => listCompanies(query) });

  const items = useMemo(
    () => (list.data?.items ?? []).filter((c) => fished || !FISHED.has(c.status)),
    [list.data, fished],
  );
  const pointsById = useMemo(() => new Map((points.data ?? []).map((p) => [p.id, p])), [points.data]);
  const visiblePoints = useMemo(() => items.flatMap((c) => pointsById.get(c.id) ?? []), [items, pointsById]);
  const inView = items.filter((c) => {
    const point = pointsById.get(c.id);
    return point !== undefined && inside(point, bounds);
  });
  const withoutLocation = config.tileUrl && points.data ? items.filter((c) => !pointsById.has(c.id)).length : 0;
  const selected = items.find((c) => c.id === selectedId) ?? null;
  const onlyTips = inView.length > 0 && inView.every((c) => c.status === "tip");

  const filters = (
    <div className={desktop ? styles.filters : styles.overlayChips} role="group" aria-label={nl.companies.filters}>
      <Chip
        active={near && hasHome}
        disabled={!hasHome}
        title={hasHome ? undefined : t.nearNeedsHome}
        onClick={() => {
          setNear(!near);
        }}
      >
        {t.nearFilter(me.data?.max_distance_km ?? null)}
      </Chip>
      <Chip
        active={onlyKans}
        onClick={() => {
          setOnlyKans(!onlyKans);
        }}
      >
        {t.onlyKans}
      </Chip>
      <Chip
        active={fished}
        title={t.fishedHelp}
        onClick={() => {
          setFished(!fished);
        }}
      >
        {t.fished}
      </Chip>
    </div>
  );

  const shown = config.tileUrl ? inView : items;
  let listBody;
  if (list.isPending) {
    listBody = (
      <div className={styles.skeletons} role="status" aria-busy="true" aria-label={nl.common.loading}>
        <Skeleton height={56} />
        <Skeleton height={56} />
      </div>
    );
  } else if (list.isError) {
    listBody = (
      <EmptyState title={t.loadError} action={<Button onClick={() => void list.refetch()}>{nl.common.retry}</Button>} />
    );
  } else if (shown.length === 0) {
    listBody = <EmptyState title={t.emptyView} />;
  } else {
    listBody = (
      <ul className={styles.list} aria-label={t.listTitle}>
        {shown.map((company) => (
          <Row
            key={company.id}
            company={company}
            selected={company.id === selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setListOpen(false);
            }}
          />
        ))}
      </ul>
    );
  }

  const map = (
    <Suspense fallback={<Skeleton height="100%" radius={0} />}>
      <MapView
        config={config}
        points={visiblePoints}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onBoundsChange={setBounds}
        zoomButtons={desktop}
      />
    </Suspense>
  );
  const card = selected && (
    <SelectedCard
      company={selected}
      onClose={() => {
        setSelectedId(null);
      }}
    />
  );

  // Mobile (< 1024 px): the whole screen is map; the list is a bottom sheet (M5-03).
  if (config.tileUrl && !desktop) {
    return (
      <div className={styles.mobile}>
        <h1 className="sr-only">{nl.screens.map}</h1>
        {map}
        <div className={styles.overlayTop}>
          <button type="button" className={styles.searchBar} onClick={openSearch}>
            <SearchIcon size={16} />
            <span>{nl.nav.search}</span>
          </button>
          {filters}
        </div>
        {card}
        {!selected && (
          <Button
            className={styles.showList}
            onClick={() => {
              setListOpen(true);
            }}
          >
            {t.showList(inView.length)}
          </Button>
        )}
        <Sheet open={listOpen} onOpenChange={setListOpen} title={t.listTitle}>
          {!hasHome && <p className={styles.muted}>{t.nearNeedsHome}</p>}
          {onlyTips && <p className={styles.tip}>{t.whiteSpots}</p>}
          {listBody}
          {withoutLocation > 0 && <p className={styles.muted}>{t.noCoordinates(withoutLocation)}</p>}
        </Sheet>
      </div>
    );
  }

  return (
    <div className={[styles.page, config.tileUrl ? "" : styles.noMap].join(" ")}>
      {config.tileUrl ? (
        <div className={styles.mapColumn}>
          {map}
          {card}
        </div>
      ) : null}
      <aside className={styles.side} aria-labelledby="map-list-title">
        <h1 className={styles.title}>{nl.screens.map}</h1>
        {!config.tileUrl && (
          <p className={styles.notice} role="status">
            {t.noTiles}
          </p>
        )}
        {filters}
        {!hasHome && <p className={styles.muted}>{t.nearNeedsHome}</p>}
        <h2 id="map-list-title" className={styles.listTitle}>
          {t.listTitle}
          {config.tileUrl && list.data && <span className={styles.muted}> · {t.listCount(inView.length)}</span>}
        </h2>
        {onlyTips && <p className={styles.tip}>{t.whiteSpots}</p>}
        {listBody}
        {withoutLocation > 0 && <p className={styles.muted}>{t.noCoordinates(withoutLocation)}</p>}
      </aside>
    </div>
  );
}
